import { Icon } from "../components/Icon";
import {
  Done,
  FlowField,
  FlowSheet,
  In,
  InButtons,
  InFlow,
  Out,
  Phone,
} from "../components/Phone";
import { ScrollStage } from "../components/ScrollStage";
import {
  DASH,
  EJOR_EXPIRY_HOURS,
  SCHEDULE_SMALL_RUN_NAIRA,
  CABLE_SERVICE_FEE_NAIRA,
} from "../config/company";

// The home page's conversations and the Ejor / Dash / scheduled-payment
// sections. Names, numbers and codes in the phones are made up; the wording
// follows Hethera's real messages (../Hethera/app/service/*), so update both
// together.

// Thousands separators by hand: toLocaleString can differ between Node
// (prerender) and the browser, which would break hydration.
export function naira(amount: number): string {
  return "₦" + String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function Facts({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="facts">
      {items.map((item, i) => (
        <li key={i}>
          <Icon name="check" size={16} color="var(--ok)" strokeWidth={2.4} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const K = ({ children }: { children: string }) => (
  <span className="keyword">{children}</span>
);

// ── Hero: an everyday purchase, PIN form included ───────────────────────────

export function HeroChat() {
  return (
    <Phone
      autoplay
      backdrop="circle"
      label="Example Hethera conversation on WhatsApp: buying data and confirming with a PIN"
    >
      <Out time="9:41">Buy 2GB MTN data for my sister, 0803 ••• 4412</Out>
      <In time="9:41">
        <span>Here are the MTN plans with 2GB:</span>
        <ul className="bubble__mono">
          <li>2GB · 1 day</li>
          <li>2GB · 2 days</li>
          <li>2GB · 30 days</li>
        </ul>
        <span>Which one should I get?</span>
      </In>
      <Out time="9:42">30 days please</Out>
      <InFlow cta="Verify PIN" icon="lock" time="9:42">
        <span>To buy mobile data, please verify your transaction PIN.</span>
      </InFlow>
      <FlowSheet title="Transaction PIN" submit="Submit">
        <span>Enter your transaction PIN to buy mobile data.</span>
        <FlowField label="Transaction PIN" value="••••••" secret />
      </FlowSheet>
      <In time="9:42">
        <Done>Done</Done>
        <span>
          2GB MTN data (30 days) sent to 0803 ••• 4412. Paid with your Visa
          ending 4081.
        </span>
      </In>
    </Phone>
  );
}

// ── Ejor: what the person asked to pay sees ─────────────────────────────────

function EjorChat() {
  return (
    <Phone
      backdrop="pair"
      clock="18:07"
      label="Example Ejor request on WhatsApp, as the person asked to pay sees it"
    >
      <InButtons buttons={["View details", "Decline"]} time="18:05">
        <span>
          Tobi (0811 ••• 5541) is asking you to help pay for GOtv Max (₦8,500).
          You're getting this because Ejor requests are on for your Hethera
          account. Tap View details to see everything before you decide. Ref
          EJ-7K2QP.
        </span>
      </InButtons>
      <Out time="18:06">View details</Out>
      <InButtons buttons={["Accept & pay", "Decline", "Block"]} time="18:06">
        <span>
          GOtv Max for smartcard 70 ••• 3321
          <br />
          {naira(8500)} + {naira(CABLE_SERVICE_FEE_NAIRA)} service fee ={" "}
          {naira(8500 + CABLE_SERVICE_FEE_NAIRA)}
          <br />
          Expires in 47 hours.
          <br />
          Ref EJ-7K2QP
        </span>
      </InButtons>
      <Out time="18:06">Accept &amp; pay</Out>
      <InFlow cta="Enter PIN" icon="lock" time="18:06">
        <span>
          To pay {naira(8500)} + {naira(CABLE_SERVICE_FEE_NAIRA)} service fee ={" "}
          {naira(8500 + CABLE_SERVICE_FEE_NAIRA)} for Tobi's GOtv Max, please enter
          your transaction PIN.
        </span>
      </InFlow>
      <In time="18:07">
        <Done>Paid</Done>
        <span>Tobi's GOtv Max is renewed. We've told Tobi you paid.</span>
      </In>
    </Phone>
  );
}

export function EjorSection() {
  return (
    <ScrollStage
      id="ejor"
      labelledBy="ejor-title"
      stepAt={[0, 0.15, 0.6]}
      phone={<EjorChat />}
      copy={
        <>
          <p className="eyebrow">Ejor</p>
          <h2 id="ejor-title" className="display">
            Ask someone you know to pay a bill.
          </h2>
          <p className="lede">
            Short on cash? Send <K>EJOR</K>, choose the bill and enter the
            WhatsApp number of one person you'd like to pay it. They see exactly
            what it's for and pay with their own card and PIN.
          </p>
          <ol className="showcase__steps">
            <li>
              Send EJOR. A form opens: pick the bill (airtime, data, cable or
              electricity) and the person.
            </li>
            <li>
              They get one message with your name, your number and the bill, and
              choose View details or Decline.
            </li>
            <li>
              If they accept, they confirm with their PIN. The bill is paid and
              you're told straight away.
            </li>
          </ol>
          <Facts
            items={[
              <>
                One bill, one person. An Ejor isn't split between several
                people: whoever accepts pays the whole bill.
              </>,
              <>
                You can only ask people who already use Hethera, have made a
                purchase and have turned Ejor on.
              </>,
              <>
                Unpaid requests expire after {EJOR_EXPIRY_HOURS} hours. To
                cancel one, send <K>CANCEL EJ-xxxxx</K> with its code.
              </>,
              <>
                Don't want requests? Send <K>EJOR OFF</K>, or tap Block on a
                request from someone.
              </>,
              <>
                Cable includes the {naira(CABLE_SERVICE_FEE_NAIRA)} service fee,
                and electricity the card processing fee, paid by the person who
                pays.
              </>,
            ]}
          />
        </>
      }
    />
  );
}

// ── Dash: what the person receiving a gift sees ─────────────────────────────

function DashChat() {
  return (
    <Phone
      backdrop="flower"
      clock="8:15"
      label="Example Dash gift on WhatsApp, as the person receiving it sees it"
    >
      <span className="chat__day">Saturday</span>
      <InButtons buttons={["Receive gift", "No thanks"]} time="8:00">
        <span>
          🎁 Tobi (0811 ••• 5541) has sent you ₦2,000 airtime for your birthday.
          Tap Receive gift to choose where it goes. It's available until 29 Nov.
        </span>
      </InButtons>
      <Out time="8:14">Receive gift</Out>
      <FlowSheet title="Receive your gift" submit="Continue">
        <span>
          Where should the airtime go? If you've moved networks, pick the one
          you use now.
        </span>
        <FlowField label="Phone number" value="0803 ••• 4412" />
        <FlowField label="Network" value="MTN" />
      </FlowSheet>
      <In time="8:15">
        <Done>Done</Done>
        <span>₦2,000 airtime is on 0803 ••• 4412.</span>
      </In>
    </Phone>
  );
}

export function DashSection() {
  return (
    <ScrollStage
      id="dash"
      labelledBy="dash-title"
      flip
      stepAt={[0, 0.12, 0.5]}
      phone={<DashChat />}
      copy={
        <>
          <p className="eyebrow">Dash</p>
          <h2 id="dash-title" className="display">
            Send airtime or electricity as a gift.
          </h2>
          <p className="lede">
            Send <K>DASH</K> to gift airtime or electricity to someone who uses
            Hethera. You pay now. On the day you choose, they get a message and
            decide where it goes: their phone number and network, or their
            meter. If they don't want it, your card is refunded in full.
          </p>
          <ol className="showcase__steps">
            <li>
              Send DASH. In the form, enter their number, the gift, the amount,
              an occasion and a short note.
            </li>
            <li>
              Pick when it arrives: now, or a day up to {DASH.maxDaysAhead} days
              ahead at {DASH.slots}. Pay with your PIN.
            </li>
            <li>
              They tap Receive gift and choose the number or meter. It's bought
              there and then, and you're told.
            </li>
          </ol>
          <Facts
            items={[
              <>
                Airtime from {naira(DASH.airtimeMin)} to{" "}
                {naira(DASH.airtimeMax)}; electricity from{" "}
                {naira(DASH.electricityMin)} to {naira(DASH.electricityMax)}{" "}
                (some meters need more). Amounts go up in {naira(DASH.step)}{" "}
                steps.
              </>,
              <>
                Airtime gifts have no fee. For electricity, the card processing
                fee comes out of what you pay and they get the rest. You see
                both before you pay.
              </>,
              <>
                If they tap No thanks, or don't accept within{" "}
                {DASH.responseDays} days, the full amount goes back to your
                card. Refunds always go to the card that paid.
              </>,
              <>
                Changed your mind? Send <K>CANCEL DS-xxxxx</K> before the
                delivery day for a full refund.
              </>,
              <>
                A gift is for one bill only. It has no cash value and can't be
                withdrawn, split or moved to another bill. To stop receiving
                gifts, send <K>DASH OFF</K>.
              </>,
            ]}
          />
        </>
      }
    />
  );
}

// ── Scheduled payments ──────────────────────────────────────────────────────

function ScheduleChat() {
  return (
    <Phone
      backdrop="arc"
      clock="8:20"
      label="Example scheduled payment on WhatsApp: the reminder the day before and the result"
    >
      <span className="chat__day">31 October</span>
      <In time="12:00">
        <span>
          Your scheduled payment of {naira(8500)} + {naira(CABLE_SERVICE_FEE_NAIRA)}{" "}
          service fee for Home GOtv will be made tomorrow, 1 Nov, around 8:00
          am, from your card ending 4081.
        </span>
      </In>
      <span className="chat__day">1 November</span>
      <In time="8:01">
        <Done>Paid</Done>
        <span>{naira(8500)} cable TV for Home GOtv.</span>
      </In>
      <Out time="8:20">SCHEDULE</Out>
      <InButtons buttons={["Pause", "Cancel"]} time="8:20">
        <span>
          SP-4TQ2M · Home GOtv
          <br />
          GOtv Max, every month on the 1st at 8:00 am
          <br />
          Next payment: 1 Dec
        </span>
      </InButtons>
    </Phone>
  );
}

export function SchedulesSection() {
  return (
    <ScrollStage
      id="schedules"
      labelledBy="schedules-title"
      phone={<ScheduleChat />}
      copy={
        <>
          <p className="eyebrow">Scheduled payments</p>
          <h2 id="schedules-title" className="display">
            Bills that pay themselves, on your terms.
          </h2>
          <p className="lede">
            Set up a payment once, such as data for Mum on the 1st of every
            month or your GOtv renewal, and confirm it with your PIN. Hethera
            pays it on the day and tells you when it's done.
          </p>
          <Facts
            items={[
              <>Once, every week or every month, at the time you choose.</>,
              <>
                You set the most a payment may cost. If a plan's price goes
                above it, that payment is skipped and you're told; nothing is
                charged.
              </>,
              <>
                We remind you the day before, except for airtime and data under{" "}
                {naira(SCHEDULE_SMALL_RUN_NAIRA)}.
              </>,
              <>
                A payment that fails is never retried. If you still want it, tap
                Pay now.
              </>,
              <>
                If your card is declined twice in a row or you add a new card,
                the schedule pauses until you resume it with your PIN.
              </>,
              <>
                Send <K>SCHEDULE</K> to see your payments and pause, skip or
                cancel any of them.
              </>,
            ]}
          />
        </>
      }
    />
  );
}

// ── Shortcuts: words Hethera answers straight away ──────────────────────────

const SHORTCUTS: { word: string; body: string }[] = [
  {
    word: "EJOR",
    body: "Ask someone you know to pay a bill. EJOR ON / EJOR OFF to allow or stop requests.",
  },
  {
    word: "DASH",
    body: "Send airtime or electricity as a gift. DASH OFF to stop receiving gifts.",
  },
  {
    word: "SCHEDULE",
    body: "See your scheduled payments and pause, skip or cancel them.",
  },
  {
    word: "REPORT",
    body: "Get a private link to charts of what you spent. REPORT STOP ends any open link.",
  },
  {
    word: "CANCEL",
    body: "CANCEL EJ-xxxxx or CANCEL DS-xxxxx cancels an Ejor or a gift by its code.",
  },
];

export function ShortcutsSection() {
  return (
    <section
      id="shortcuts"
      className="container section"
      style={{ paddingTop: 0 }}
      aria-labelledby="shortcuts-title"
    >
      <div className="section-head">
        <div>
          <p className="eyebrow">Shortcuts</p>
          <h2
            id="shortcuts-title"
            className="display"
            style={{ fontSize: "clamp(32px, 3.6vw, 44px)" }}
          >
            Words Hethera answers straight away.
          </h2>
        </div>
        <p className="lede">
          Or just write what you need in your own words. These are quicker.
        </p>
      </div>
      <ul className="shortcuts">
        {SHORTCUTS.map((s) => (
          <li key={s.word}>
            <span>
              <K>{s.word}</K>
            </span>
            {s.body}
          </li>
        ))}
      </ul>
    </section>
  );
}
